// src/context/CampaignContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { rtdb } from '../firebase/config';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';

const CampaignContext = createContext();

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
};

export const CampaignProvider = ({ campaignId, children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);

  // GM Mode state
  const [gmModeSettings, setGmModeSettings] = useState(null);
  const [dcQueue, setDcQueue] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [sharedGameState, setSharedGameState] = useState({});

  useEffect(() => {
    if (!campaignId || !user) {
      setConnectionStatus('disconnected');
      return;
    }

    let connectRef, participantsRef, unsubscribeConnect, unsubscribeParticipants;

    // Set up connection status listener
    connectRef = ref(rtdb, '.info/connected');
    unsubscribeConnect = onValue(connectRef, (snapshot) => {
      const connected = snapshot.val();
      setIsConnected(connected);
      setConnectionStatus(connected ? 'connected' : 'disconnected');

      if (connected) {
        // Set participant data when connected
        const userParticipantRef = ref(rtdb, `campaigns/${campaignId}/participants/${user.uid}`);
        const participantData = {
          displayName: user.displayName || user.email || 'Unknown User',
          lastSeen: serverTimestamp(),
          status: 'active'
        };

        set(userParticipantRef, participantData)
          .then(() => {
            // Set up disconnect cleanup
            onDisconnect(userParticipantRef).remove()
              .catch((error) => {
                console.error('CampaignContext: Error setting disconnect cleanup:', error);
              });
          })
          .catch((error) => {
            console.error('CampaignContext: Error setting participant data:', error);
          });
      }
    });

    // Set up participants listener (who's currently active in the campaign)
    participantsRef = ref(rtdb, `campaigns/${campaignId}/participants`);
    unsubscribeParticipants = onValue(participantsRef, (snapshot) => {
      const participantsData = snapshot.val();

      if (participantsData) {
        const participantsList = Object.entries(participantsData).map(([userId, data]) => ({
          userId,
          ...data
        }));
        setParticipants(participantsList);
      } else {
        setParticipants([]);
      }
    });

    // Set up GM Mode listeners
    const gmModeRef = ref(rtdb, `campaigns/${campaignId}/gmMode`);
    const unsubscribeGmMode = onValue(gmModeRef, (snapshot) => {
      const gmData = snapshot.val();

      if (gmData) {
        // GMDashboard sets 'active' field, use that as gmModeSettings
        setGmModeSettings(gmData.active ? { active: true } : null);
        const queueArray = gmData.dcQueue ? Object.values(gmData.dcQueue) : [];
        setDcQueue(queueArray);
        setActivityLog(gmData.activityLog ? Object.values(gmData.activityLog).slice(-50) : []); // Keep last 50 activities
      } else {
        setGmModeSettings(null);
        setDcQueue([]);
      }
    });

    // Set up shared game state listener
    const gameStateRef = ref(rtdb, `campaigns/${campaignId}/gameState`);
    const unsubscribeGameState = onValue(gameStateRef, (snapshot) => {
      const stateData = snapshot.val();
      setSharedGameState(stateData || {});
    });

    return () => {
      if (unsubscribeConnect) unsubscribeConnect();
      if (unsubscribeParticipants) unsubscribeParticipants();
      if (unsubscribeGmMode) unsubscribeGmMode();
      if (unsubscribeGameState) unsubscribeGameState();
    };
  }, [campaignId, user]);

  // GM Mode functions
  const updateGmModeSettings = async (settings) => {
    if (!campaignId) return;
    const settingsRef = ref(rtdb, `campaigns/${campaignId}/gmMode/settings`);
    await set(settingsRef, settings);
  };

  const addToDcQueue = async (dcEntry) => {
    if (!campaignId) return;
    const queueRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue/${dcEntry.id}`);
    await set(queueRef, dcEntry);
  };

  const removeFromDcQueue = async (entryId) => {
    if (!campaignId) return;
    const entryRef = ref(rtdb, `campaigns/${campaignId}/gmMode/dcQueue/${entryId}`);
    await set(entryRef, null);
  };

  const logActivity = async (type, message, details = {}) => {
    if (!campaignId) return;
    const activityRef = ref(rtdb, `campaigns/${campaignId}/gmMode/activityLog/${Date.now()}`);
    await set(activityRef, {
      type,
      message,
      details,
      timestamp: serverTimestamp(),
      userId: user.uid,
      userName: user.displayName || user.email
    });
  };

  const updateSharedGameState = async (key, value) => {
    if (!campaignId) return;
    const stateRef = ref(rtdb, `campaigns/${campaignId}/gameState/${key}`);
    await set(stateRef, value);
  };

  const value = {
    // Campaign ID
    campaignId,

    // Connection state
    isConnected,
    connectionStatus,
    error,

    // Real-time data
    participants,

    // GM Mode state
    gmModeSettings,
    dcQueue,
    activityLog,
    sharedGameState,

    // GM Mode functions
    updateGmModeSettings,
    addToDcQueue,
    removeFromDcQueue,
    logActivity,
    updateSharedGameState
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignContext;